// etherNav — the pure half of the mini navigator inside a sheet.
//
// Who answers an arrow key, which way a step went, how a swipe on the body
// decides it is horizontal, and where the ticker's words sit. No DOM at import
// time and no React: EtherModal and MiniNav call these, etherNav.test.ts pins
// them. An element is only ever asked for `tagName`, `isContentEditable`,
// `type`, `closest()` and its scroll box, so a test can hand in a plain object.

/** the least of an Element these helpers read */
export interface ElementLike {
  tagName?: string;
  isContentEditable?: boolean;
  type?: string;
  closest?: (selector: string) => unknown;
  parentElement?: ElementLike | null;
  scrollWidth?: number;
  clientWidth?: number;
  scrollLeft?: number;
}

/** the least of a KeyboardEvent the step guard reads */
export interface KeyLike {
  key: string;
  defaultPrevented?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  isComposing?: boolean;
}

/**
 * Anything that owns its own arrow keys. A press inside one of these is never a
 * step between siblings: a field moves its caret, the chips move between
 * themselves (the tab pattern), the chapter rail walks its cards, a slider
 * slides. `[data-own-arrows]` is the opt-out for anything new.
 */
export const OWN_ARROWS =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="tablist"], [role="slider"], [role="listbox"], [role="menu"], [role="menubar"], [role="radiogroup"], [role="grid"], [role="tree"], [role="spinbutton"], .st-rail, .st-rail-wrap, [data-own-arrows]';

/**
 * Where a finger on a sheet's body never starts a sideways step: a field, the
 * chapter rail (it swipes its own cards), anything that says so. The sheet's
 * own root carries [data-noswipe] for the shell's sake, so that attribute is
 * checked separately, below the root.
 */
export const OWN_SWIPE = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), .st-rail-wrap, [data-own-swipe], [data-hscroll]';

function typing(el: ElementLike | null | undefined): boolean {
  if (!el) return false;
  const tag = (el.tagName ?? "").toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}

/**
 * Which way a key press steps between siblings: -1 (←), 1 (→) or 0 (not ours).
 * Handled in ONE place (EtherModal's panel, or a standalone MiniNav), so a press
 * never steps twice; a handler deeper in the tree that already used the key
 * (ChapterRail, the chips) marks it with preventDefault and this steps aside.
 */
export function stepFromKey(e: KeyLike, target: ElementLike | null | undefined): -1 | 0 | 1 {
  if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return 0;
  if (e.defaultPrevented || e.isComposing) return 0;
  // a modifier means something else: word jumps, selection, history, spaces
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return 0;
  if (typing(target)) return 0;
  if (target && typeof target.closest === "function" && target.closest(OWN_ARROWS)) return 0;
  return e.key === "ArrowLeft" ? -1 : 1;
}

/**
 * Which way a sheet steps on a key press: `stepFromKey`, and only when there is
 * a sibling that way and the sheet is still live. A sheet on its way out never
 * steps (a press during the exit would reopen it on the next sibling). The
 * sheet stops every ← → from reaching the page behind whatever this answers
 * (EtherModal).
 */
export function sheetStep(
  e: KeyLike,
  target: ElementLike | null | undefined,
  steps: { prev?: unknown; next?: unknown } | null | undefined,
  live: boolean,
): -1 | 0 | 1 {
  if (!live || !steps) return 0;
  const dir = stepFromKey(e, target);
  if (dir < 0) return steps.prev ? -1 : 0;
  if (dir > 0) return steps.next ? 1 : 0;
  return 0;
}

/**
 * What ← → (and a sideways swipe) move between in a sheet: its siblings when it
 * has steps, otherwise its sections (law_sheet_rises: a multi-screen sheet's
 * subjects are its navigator, and the arrows walk them, wrapping as the
 * navigator's words do). Null when there is nothing to step to.
 */
export function sheetSteps<S extends { onClick: () => void }>(
  nav:
    | {
        prev?: S | null;
        next?: S | null;
        sections?: readonly { id: string }[] | null;
        section?: string | null;
        onSection?: ((id: string) => void) | null;
      }
    | null
    | undefined,
): { prev?: S | { label: string; onClick: () => void } | null; next?: S | { label: string; onClick: () => void } | null } | null {
  if (!nav) return null;
  if (nav.prev || nav.next) return { prev: nav.prev, next: nav.next };
  const ids = nav.sections?.map((s) => s.id) ?? [];
  const go = nav.onSection;
  if (ids.length < 2 || !go) return null;
  const at = Math.max(0, ids.indexOf(nav.section ?? ids[0]));
  return {
    prev: { label: "previous section", onClick: () => go(ids[wrapIndex(at, -1, ids.length)]) },
    next: { label: "next section", onClick: () => go(ids[wrapIndex(at, 1, ids.length)]) },
  };
}

// ── the Tab trap ─────────────────────────────────────────────────────────────

/** a real stop in the Tab order: a control taken out of it (tabIndex -1: the ticker's words, the chips at rest) is not */
export function isTabStop(el: { tabIndex?: number } | null | undefined): boolean {
  return !!el && (el.tabIndex ?? 0) >= 0;
}

/**
 * Where Tab wraps inside a sheet, or null to let the browser move focus on.
 * `before` and `after` count the real stops either side of what holds focus
 * (which may be the panel itself, or a control out of the Tab order). Focus
 * that would run off either end wraps round, so it never leaves the sheet;
 * with no stops at all it stays where it is.
 */
export function tabWrap(total: number, before: number, after: number, shift: boolean): "first" | "last" | "stay" | null {
  if (total <= 0) return "stay";
  if (shift) return before <= 0 ? "last" : null;
  return after <= 0 ? "first" : null;
}

/**
 * The direction a move from index `from` to index `to` travelled in a list of
 * `n` that wraps (the navigator's rule): the one-step wrap from the last to the
 * first reads as forward, and from the first to the last as back.
 * 0 when nothing moved or either index is unknown.
 */
export function stepDirection(from: number, to: number, n: number): -1 | 0 | 1 {
  if (from < 0 || to < 0 || from === to) return 0;
  const delta = to - from;
  if (n > 2 && Math.abs(delta) === n - 1) return delta > 0 ? -1 : 1;
  return delta > 0 ? 1 : -1;
}

/** the sibling `dir` steps away from `index`, wrapping; -1 when there is nowhere to go */
export function wrapIndex(index: number, dir: -1 | 1, n: number): number {
  if (n < 2 || index < 0) return -1;
  return (index + dir + n) % n;
}

export interface SiblingSteps<T> {
  index: number;
  total: number;
  prev: T | null;
  next: T | null;
}

/**
 * Where `activeKey` sits in `items` and who stands either side of it (the list
 * wraps, so every sibling is reachable both ways). A key that is not in the
 * list, or a list of one, has no steps.
 */
export function siblingSteps<T>(items: readonly T[], activeKey: string | null | undefined, keyOf: (t: T) => string): SiblingSteps<T> {
  const total = items.length;
  const index = activeKey == null ? -1 : items.findIndex((t) => keyOf(t) === activeKey);
  if (index < 0 || total < 2) return { index, total, prev: null, next: null };
  return { index, total, prev: items[wrapIndex(index, -1, total)], next: items[wrapIndex(index, 1, total)] };
}

// ── the body swipe (touch only) ─────────────────────────────────────────────

/** how far a finger travels before the gesture declares its axis */
export const SWIPE_SLOP = 10;
/** a horizontal travel this long commits a step (the shell's own threshold) */
export const SWIPE_COMMIT = 56;

/**
 * Has the finger shown its intent yet? `null` while inside the slop; `"x"` once
 * it is clearly sideways (x beats y by a fifth), otherwise `"y"`: the scroller
 * owns vertical, and a gesture that starts vertical never turns into a step.
 */
export function swipeAxis(dx: number, dy: number, slop = SWIPE_SLOP): "x" | "y" | null {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (ax < slop && ay < slop) return null;
  return ax > ay * 1.2 ? "x" : "y";
}

/** a released horizontal swipe → the step it asks for: finger left = next (1), right = previous (-1) */
export function swipeStep(dx: number, threshold = SWIPE_COMMIT): -1 | 0 | 1 {
  if (dx <= -threshold) return 1;
  if (dx >= threshold) return -1;
  return 0;
}

/**
 * Can something between the finger and the sheet still scroll sideways in the
 * way the finger is going? Then the swipe is that region's, not ours (a wide
 * code block, a strip). `dx > 0` is a finger moving right.
 */
export function canScrollX(el: ElementLike | null | undefined, dx: number, stop?: ElementLike | null): boolean {
  let cur: ElementLike | null | undefined = el;
  while (cur && cur !== stop) {
    const sw = cur.scrollWidth ?? 0;
    const cw = cur.clientWidth ?? 0;
    if (sw > cw + 2) {
      const left = cur.scrollLeft ?? 0;
      if (dx > 0 && left > 0) return true;
      if (dx < 0 && left + cw < sw - 1) return true;
    }
    cur = cur.parentElement ?? null;
  }
  return false;
}

// ── the ticker (the navigator's words, one size down) ───────────────────────

/** px of travel before a press on the ticker becomes a drag */
export const TICKER_DRAG_START = 4;
/** px of travel that commits a step (the navigator's 44) */
export const TICKER_COMMIT = 44;

export interface TickerWord {
  /** index into the sibling list */
  i: number;
  /** a copy of a word that slid off the left, riding again on the right */
  wrapped: boolean;
  /** the word in full ink */
  active: boolean;
  /** slid off the left: keeps a stepped blur and a deepening fade */
  past: boolean;
  /** resting depth (the CSS reads it as --nw-op) */
  op: number;
  /** px, stepped, never tweened (law_snappy) */
  blur: number;
}

/**
 * The words the ticker lays out, in order: every sibling once, then the ones
 * before the active word again (they slid off the left and ride back in from
 * the right), exactly OsNavigator's arithmetic so a sheet's ticker and the
 * dashboard's read as one instrument.
 */
export function tickerWords(n: number, activeIdx: number): TickerWord[] {
  if (n <= 0) return [];
  const a = Math.min(Math.max(0, activeIdx), n - 1);
  const out: TickerWord[] = [];
  for (let gi = 0; gi < n + a; gi++) {
    const wrapped = gi >= n;
    const rel = gi - a;
    const active = gi === a;
    const past = rel < 0 && !wrapped;
    out.push({
      i: wrapped ? gi - n : gi,
      wrapped,
      active,
      past,
      op: active ? 1 : past ? Math.max(0, 0.42 + rel * 0.16) : 0.46,
      blur: past ? Math.min(7, -rel * 3.2) : 0,
    });
  }
  return out;
}

/** a released ticker drag → the step it commits: pulled left = next */
export function tickerStep(dx: number, threshold = TICKER_COMMIT): -1 | 0 | 1 {
  if (dx <= -threshold) return 1;
  if (dx >= threshold) return -1;
  return 0;
}

// ── the crumb on a narrow sheet ──────────────────────────────────────────────

export interface CrumbFit {
  /** each place's words at their natural width, in order (the last is where you are) */
  widths: readonly number[];
  /** the width the crumb has */
  room: number;
  /** what one "/" costs: the mark and the gap either side of it */
  sep: number;
  /** the "…" that stands in for the places folded away */
  fold: number;
  /** where you are may give down to this (never below its own words) */
  hereFloor: number;
  /** places already gone before any fitting (a phone drops the way out, which the close button is) */
  first?: number;
}

/**
 * The first place a crumb shows when its trail does not fit its room. A way
 * back is its whole words or it folds away whole, never an ellipsis cut to a
 * sliver ("br…"): the earliest places fold first, and once any has folded a
 * "…" stands in for them (a step back to the nearest one). Where you are always
 * stays, and is the only place that gives (down to `hereFloor`). When even
 * "… / where you are" is too wide, where you are keeps the room alone.
 */
export function crumbFit({ widths, room, sep, fold, hereFloor, first = 0 }: CrumbFit): number {
  const n = widths.length;
  if (n <= 1) return 0;
  const last = n - 1;
  const from0 = Math.min(Math.max(0, first), last);
  const here = Math.min(widths[last], Math.max(0, hereFloor));
  for (let k = from0; k < last; k++) {
    let need = here;
    for (let i = k; i < last; i++) need += widths[i] + sep;
    if (k > from0) need += fold + sep;
    // half a pixel of grace for sub-pixel widths
    if (need <= room + 0.5) return k;
  }
  return last;
}
